import React, { useState, useEffect } from 'react';
import { Rapport, reportService, UpdateRapportRequest } from '../../services/reportService';
import { useModal } from '../../contexts/ModalContext';
import ReportEditForm from './ReportEditForm';
import './ReportList.css';

const ReportList: React.FC = () => {
  const [reports, setReports] = useState<Rapport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingReport, setEditingReport] = useState<Rapport | null>(null);
  const [updating, setUpdating] = useState(false);
  const { showModal } = useModal();

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await reportService.getAllReports();
      setReports(data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des rapports');
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (report: Rapport) => {
    setEditingReport(report);
  };

  const handleUpdateReport = async (data: UpdateRapportRequest) => {
    if (!editingReport) return;
    
    setUpdating(true);
    try {
      await reportService.updateReport(editingReport.id, data);
      await loadReports();
      setEditingReport(null);
      showModal({
        title: 'Rapport mis à jour',
        message: `Le rapport "${data.nomRapport}" a été mis à jour avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
    } catch (err: any) {
      showModal({
        title: 'Erreur de mise à jour',
        message: err.response?.data?.message || 'Erreur lors de la mise à jour du rapport',
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleReplaceFile = async (file: File) => {
    if (!editingReport) return;
    
    setUpdating(true);
    try {
      await reportService.replaceReportFile(editingReport.id, file);
      await loadReports();
      setEditingReport(null);
      showModal({
        title: 'Fichier remplacé',
        message: `Le fichier du rapport "${editingReport.nomRapport}" a été remplacé avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
    } catch (err: any) {
      showModal({
        title: 'Erreur de remplacement',
        message: err.response?.data?.message || 'Erreur lors du remplacement du fichier',
        type: 'error',
        confirmText: 'OK'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleStatus = async (id: number) => {
    const report = reports.find(r => r.id === id);
    const action = report?.isDisabled ? 'activer' : 'désactiver';
    
    showModal({
      title: `Confirmer ${action}`,
      message: `Êtes-vous sûr de vouloir ${action} le rapport "${report?.nomRapport}" ?`,
      type: 'confirm',
      confirmText: action === 'activer' ? 'Activer' : 'Désactiver',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await reportService.toggleReportStatus(id);
          await loadReports();
          showModal({
            title: `Rapport ${action}`,
            message: `Le rapport a été ${action} avec succès.`,
            type: 'success',
            confirmText: 'OK'
          });
        } catch (err: any) {
          showModal({
            title: 'Erreur de changement de statut',
            message: err.response?.data?.message || 'Erreur lors du changement de statut',
            type: 'error',
            confirmText: 'OK'
          });
        }
      }
    });
  };

  const handleDelete = async (id: number) => {
    const report = reports.find(r => r.id === id);
    showModal({
      title: 'Confirmer la suppression',
      message: `Êtes-vous sûr de vouloir supprimer le rapport "${report?.nomRapport}" ? Cette action est irréversible.`,
      type: 'confirm',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await reportService.deleteReport(id);
          await loadReports();
          showModal({
            title: 'Rapport supprimé',
            message: 'Le rapport a été supprimé avec succès.',
            type: 'success',
            confirmText: 'OK'
          });
        } catch (err) {
          showModal({
            title: 'Erreur de suppression',
            message: 'Erreur lors de la suppression du rapport',
            type: 'error',
            confirmText: 'OK'
          });
          console.error('Error deleting report:', err);
        }
      }
    });
  };

  const handleDownload = async (id: number, nomRapport: string) => {
    try {
      const blob = await reportService.downloadReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nomRapport}.trdp`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Erreur lors du téléchargement du rapport');
      console.error('Error downloading report:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return <div className="loading">Chargement des rapports...</div>;
  }

  return (
    <div className="report-list">
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {editingReport && (
        <ReportEditForm
          report={editingReport}
          onSubmit={handleUpdateReport}
          onCancel={() => setEditingReport(null)}
          onReplaceFile={handleReplaceFile}
          loading={updating}
        />
      )}

      <div className="report-grid">
        {reports.map((report) => (
          <div key={report.id} className="report-card">
            <div className="report-card-header">
              <h3>{report.nomRapport}</h3>
              <div className="report-actions">
                <button 
                  className="action-btn edit"
                  onClick={() => handleEdit(report)}
                  title="Modifier"
                >
                  ✏️
                </button>
                <button 
                  className="action-btn toggle"
                  onClick={() => handleToggleStatus(report.id)}
                  title={report.isDisabled ? "Activer" : "Désactiver"}
                >
                  {report.isDisabled ? "🔓" : "🔒"}
                </button>
                <button 
                  className="action-btn download"
                  onClick={() => handleDownload(report.id, report.nomRapport)}
                  title="Télécharger"
                >
                  📥
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => handleDelete(report.id)}
                  title="Supprimer"
                >
                  🗑️
                </button>
              </div>
            </div>
            
            <div className="report-card-body">
              <div className="report-info">
                <label>Dossier :</label>
                <span>{report.dossier}</span>
              </div>
              
              <div className="report-info">
                <label>Fichier :</label>
                <span className="file-path" title={report.fichiers}>
                  {report.fichiers.split('\\').pop() || report.fichiers}
                </span>
              </div>
              
              <div className="report-info">
                <label>Statut :</label>
                <span className={`status ${report.isDisabled ? 'disabled' : 'active'}`}>
                  {report.isDisabled ? 'Désactivé' : 'Activé'}
                </span>
              </div>
              
              <div className="report-info">
                <label>Taille :</label>
                <span>{formatFileSize(report.tailleFichier)}</span>
              </div>
              
              <div className="report-info">
                <label>Date d'ajout :</label>
                <span>{new Date(report.dateAjout).toLocaleDateString('fr-FR')}</span>
              </div>
              
              {report.dateModification && (
                <div className="report-info">
                  <label>Dernière modification :</label>
                  <span>{new Date(report.dateModification).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
              
              {report.description && (
                <div className="report-info">
                  <label>Description :</label>
                  <span>{report.description}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {reports.length === 0 && (
        <div className="empty-state">
          <p>Aucun rapport trouvé. Uploadez votre premier rapport pour commencer.</p>
        </div>
      )}
    </div>
  );
};

export default ReportList;


